import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { tokenManager } from "@/utils/tokenManager";
import { deploymentApi, usageApi } from "@/services/omnimizer-api-service";

const RAG_API_URL =
  window.env?.REACT_APP_RAG_API_URL || "http://localhost:8002/api/v1";

const statsApi = axios.create({ baseURL: RAG_API_URL });
tokenManager.setupInterceptors(statsApi);

export type StatsFilters = {
  startDate: Date;
  endDate: Date;
  userIds?: string[];
  projectIds?: string[];
  balanceId?: string | null;
};

export type MetricScope = "usage" | "usage_and_cost";

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * Serializes a Date as a naive ISO-8601 datetime string (no timezone suffix),
 * e.g. "2026-04-22T10:30:00". The backend treats naive datetimes as UTC and
 * compares them against UTC billing periods, so the wall-clock we emit must be
 * the UTC one - using local getHours() here would offset every window by the
 * viewer's timezone and push it outside the billing period (0% cost for any
 * non-UTC user).
 */
const toNaiveDateTime = (d: Date) =>
  `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` +
  `T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;

const buildParams = (filters: StatsFilters) => {
  const params = new URLSearchParams();
  params.set("start_date", toNaiveDateTime(filters.startDate));
  params.set("end_date", toNaiveDateTime(filters.endDate));
  filters.userIds
    ?.filter(Boolean)
    .forEach((id) => params.append("user_ids", id));
  filters.projectIds
    ?.filter(Boolean)
    .forEach((id) => params.append("project_ids", id));
  if (filters.balanceId) params.set("balance_id", filters.balanceId);
  return params;
};

const filterKey = (f: StatsFilters) => ({
  start: toNaiveDateTime(f.startDate),
  end: toNaiveDateTime(f.endDate),
  users: f.userIds ?? [],
  projects: f.projectIds ?? [],
  balance: f.balanceId ?? null,
});

export type RequestStats = {
  total_requests: number;
  success_requests: number;
  failed_requests: number;
  used_requests: number;
  billing_period?: {
    start_date: string;
    end_date: string;
  };
  limits?: {
    used_requests?: number;
  };
};

export type TokenStats = {
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_cost: number;
  spent_usd: number;
  limits?: { budget_usd?: number };
  billing_period?: {
    start_date: string;
    end_date: string;
  };
};

export type UsageStats = {
  total_runtime: number;
  total_runtime_cost: number;
  total_queries: number;
  total_queries_cost: number;
  limits?: {
    total_runtime?: number;
    total_runtime_cost?: number;
    total_queries?: number;
    total_queries_cost?: number;
  };
  billing_period?: {
    start_date: string;
    end_date: string;
  };
};

export type SpendStats = {
  total_spend: number;
  org_balance: number;
  limits?: { total_spend?: number };
  billing_period?: {
    start_date: string;
    end_date: string;
  };
};

export type TokensOverTimePoint = {
  date: string;
  bucket_start: string;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
};

export type RequestsOverTimePoint = {
  date: string;
  bucket_start: string;
  total: number;
  successful: number;
  failed: number;
};

export type OverTimeInterval = "hour" | "day";
export type OverTimeCategory =
  | "apis"
  | "models"
  | "deployments"
  | "flows"
  | "rags";

export type OverTimeResponse<T> = {
  interval?: OverTimeInterval;
  data: T[];
  unavailable_metrics?: string[];
};

export type DailySpendPoint = {
  date: string;
  bucket_start?: string;
  models?: number;
  flows?: number;
  rags?: number;
  api_calls?: number;
  deployments?: number;
  total_spend?: number;
  projects?: Array<{
    project_id: string;
    project_name: string;
    total_spend: number;
  }>;
  unattributed_spend?: number;
};

export type DailySpendResponse = {
  spend_complete?: boolean;
  unavailable_categories?: Array<
    "models" | "flows" | "rags" | "api_calls" | "deployments"
  >;
  interval?: OverTimeInterval;
  data: DailySpendPoint[];
};

export type TopApiKey = {
  project_id: string;
  api_key_name: string;
  total_calls: number;
  successful_requests: number;
  failed_requests: number;
  total_spend?: number;
};

export type TopModel = {
  llm_config_id: string;
  model: string;
  model_id: string;
  provider: string | null;
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_spend?: number;
};

export type TopFlow = {
  flow_id: string;
  flow_name: string;
  user_id: string;
  total_runs: number;
  successful_runs: number;
  failed_runs: number;
  total_running_time_ms: number;
  total_spend?: number;
};

export type TopDeployment = {
  deployment_id: string;
  deployment_name: string;
  total_requests: number;
  gpu_hours: number;
  total_spend?: number;
};

export type GpuComposition = {
  usage: {
    quota: OrganizationQuota | null;
    deployments: Array<{
      deployment_id: string;
      deployment_name: string;
      total_requests: number;
      total_gpu_hours: number;
      spend: string | number;
    }>;
  } | null;
  usage_available: boolean;
};

export type TopProject = {
  project_id: string;
  project_name: string;
  total_spend?: number;
  successful_requests: number;
  failed_requests: number;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
};

export type TopProjectsResponse = {
  spend_complete?: boolean;
  unavailable_categories?: string[];
  data: TopProject[];
};

export type TopProvider = {
  provider: string | null;
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_spend?: number;
};

export type ProviderTotals = {
  total_requests: number;
  successful_requests: number;
  failed_requests: number;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_spend?: number;
};

export type TopProvidersResponse = {
  data: TopProvider[];
  totals: ProviderTotals;
};

export type LlmConfigType =
  | "chat"
  | "multimodal"
  | "embedding"
  | "stt"
  | "parsing"
  | "tts"
  | "image";

export type LlmConfigSource = "user" | "public" | "project";

export type LlmConfigRow = {
  llm_config_id: string;
  provider: string | null;
  model: string;
  model_id: string;
  source: LlmConfigSource;
  type: LlmConfigType | string;
  requests: number;
  spend?: number;
};

export type LlmConfigOrderBy =
  | "provider"
  | "model"
  | "type"
  | "requests"
  | "spend";

export type LlmConfigsParams = {
  providers?: string[];
  sources?: LlmConfigSource[];
  types?: string[];
  search?: string;
  orderBy?: LlmConfigOrderBy;
  orderDir?: "asc" | "desc";
  page: number;
  pageSize: number;
};

export type LlmConfigsResponse = {
  data: LlmConfigRow[];
  metadata: {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
  };
};

export type LookupItem = { value: string; label: string };
export type PlanInfo = {
  code: string;
  name: string;
  expires_at: string | null;
  period_start: string | null;
  period_end: string | null;
};

export type OrganizationQuota = {
  used: number;
  total: number;
  available: number;
  period_start: string;
  period_end: string;
  seconds_until_reset: number;
};

const fetchStats = async <T>(
  path: string,
  params: URLSearchParams,
): Promise<T> => {
  const { data } = await statsApi.get<T>(path, { params });
  return data;
};

const fetchList = async <T>(
  path: string,
  params: URLSearchParams,
): Promise<T[]> => {
  const { data } = await statsApi.get<{ data: T[] }>(path, { params });
  return data.data ?? [];
};

export function useRequestStats(filters: StatsFilters) {
  return useQuery({
    queryKey: ["stats", "requests", filterKey(filters)],
    queryFn: () =>
      fetchStats<RequestStats>("/stats/requests/", buildParams(filters)),
  });
}

export function useTokenStats(filters: StatsFilters) {
  return useQuery({
    queryKey: ["stats", "tokens", filterKey(filters)],
    queryFn: () =>
      fetchStats<TokenStats>("/stats/tokens/", buildParams(filters)),
  });
}

export function useUsageStats(filters: StatsFilters) {
  return useQuery({
    queryKey: ["stats", "usage", filterKey(filters)],
    queryFn: () =>
      fetchStats<UsageStats>("/stats/usage/", buildParams(filters)),
  });
}

export function useSpendStats(filters: StatsFilters, enabled = true) {
  return useQuery({
    queryKey: ["stats", "spend", filterKey(filters)],
    queryFn: () =>
      fetchStats<SpendStats>("/stats/spend/", buildParams(filters)),
    enabled,
  });
}

const buildOverTimeParams = (
  filters: StatsFilters,
  category: OverTimeCategory,
  interval: OverTimeInterval,
) => {
  const params = buildParams(filters);
  params.set("category", category);
  params.set("interval", interval);
  params.set("metric_scope", "usage_and_cost");
  return params;
};

const fetchOverTime = async <T>(
  path: string,
  params: URLSearchParams,
): Promise<OverTimeResponse<T>> => {
  const { data } = await statsApi.get<OverTimeResponse<T>>(path, { params });
  return data;
};

export function useTokensOverTime(
  filters: StatsFilters,
  category: OverTimeCategory = "apis",
  interval: OverTimeInterval = "day",
  enabled = true,
) {
  return useQuery({
    queryKey: [
      "stats",
      "tokens-over-time",
      filterKey(filters),
      category,
      interval,
    ],
    queryFn: () =>
      fetchOverTime<TokensOverTimePoint>(
        "/stats/tokens/over-time",
        buildOverTimeParams(filters, category, interval),
      ),
    enabled,
  });
}

export function useRequestsOverTime(
  filters: StatsFilters,
  category: OverTimeCategory = "apis",
  interval: OverTimeInterval = "day",
  enabled = true,
) {
  return useQuery({
    queryKey: [
      "stats",
      "requests-over-time",
      filterKey(filters),
      category,
      interval,
    ],
    queryFn: () =>
      fetchOverTime<RequestsOverTimePoint>(
        "/stats/requests-over-time/",
        buildOverTimeParams(filters, category, interval),
      ),
    enabled,
  });
}

export function useDailySpend(
  filters: StatsFilters,
  interval: OverTimeInterval = "day",
  resourceTypes: string[] = [],
) {
  return useQuery({
    queryKey: [
      "stats",
      "daily-spend",
      filterKey(filters),
      interval,
      resourceTypes,
    ],
    queryFn: () => {
      const params = buildParams(filters);
      params.set("interval", interval);
      resourceTypes.filter(Boolean).forEach((type) => {
        params.append("resource_types", type);
      });
      return fetchStats<DailySpendResponse>("/stats/daily-spend/", params);
    },
  });
}

type TopParams = StatsFilters & { limit: number; sortBy: string };

const buildTopParams = (filters: TopParams) => {
  const params = buildParams(filters);
  params.set("sort_by", filters.sortBy);
  params.set("limit", String(filters.limit));
  return params;
};

export function useTopApiKeys(
  filters: StatsFilters,
  limit: number,
  metricScope: MetricScope = "usage_and_cost",
) {
  return useQuery({
    queryKey: ["stats", "top-api-keys", limit, metricScope, filterKey(filters)],
    queryFn: () => {
      const params = buildTopParams({
        ...filters,
        limit,
        sortBy: metricScope === "usage" ? "total_calls" : "total_spend",
      });
      params.set("metric_scope", metricScope);
      return fetchList<TopApiKey>("/stats/api-keys/top", params);
    },
  });
}

export function useTopModels(
  filters: StatsFilters,
  limit: number,
  metricScope: MetricScope = "usage_and_cost",
) {
  return useQuery({
    queryKey: ["stats", "top-models", limit, metricScope, filterKey(filters)],
    queryFn: () => {
      const params = buildTopParams({
        ...filters,
        limit,
        sortBy: metricScope === "usage" ? "total_requests" : "total_spend",
      });
      params.set("metric_scope", metricScope);
      return fetchList<TopModel>("/stats/models/top", params);
    },
  });
}

export function useTopFlows(
  filters: StatsFilters,
  limit: number,
  metricScope: MetricScope = "usage_and_cost",
) {
  return useQuery({
    queryKey: ["stats", "top-flows", limit, metricScope, filterKey(filters)],
    queryFn: () => {
      const params = buildTopParams({
        ...filters,
        limit,
        sortBy: metricScope === "usage" ? "total_runs" : "total_spend",
      });
      params.set("metric_scope", metricScope);
      return fetchList<TopFlow>("/stats/flows/top", params);
    },
  });
}

export function useGpuStats(
  filters: StatsFilters,
  organizationId: string | null,
  limit: number,
  enabled = true,
) {
  return useQuery({
    queryKey: ["stats", "gpu", organizationId, limit, filterKey(filters)],
    queryFn: async ({ signal }) => {
      const params = buildParams(filters);
      params.delete("balance_id");
      params.set("limit", String(limit));
      const { data } = await statsApi.get<GpuComposition>("/stats/gpu/", {
        params,
        signal,
        headers: { "X-Organization-ID": organizationId || undefined },
      });
      return data;
    },
    enabled,
  });
}

export type GpuStatsQuery = ReturnType<typeof useGpuStats>;

export function useTopProviders(
  filters: StatsFilters,
  limit = 10,
  metricScope: MetricScope = "usage_and_cost",
) {
  const query = useQuery({
    queryKey: [
      "stats",
      "top-providers",
      limit,
      metricScope,
      filterKey(filters),
    ],
    queryFn: () => {
      const params = buildTopParams({
        ...filters,
        limit,
        sortBy: metricScope === "usage" ? "total_requests" : "total_spend",
      });
      params.set("metric_scope", metricScope);
      return fetchStats<TopProvidersResponse>("/stats/providers/top", params);
    },
  });
  return { ...query, metricScope };
}

export function useLlmConfigs(
  filters: StatsFilters,
  params: LlmConfigsParams & { metricScope?: MetricScope },
) {
  return useQuery({
    queryKey: [
      "stats",
      "llm-configs",
      filterKey(filters),
      params.providers ?? [],
      params.sources ?? [],
      params.types ?? [],
      params.search ?? "",
      params.orderBy ?? "spend",
      params.orderDir ?? "desc",
      params.metricScope ?? "usage_and_cost",
      params.page,
      params.pageSize,
    ],
    queryFn: async () => {
      const query = buildParams(filters);
      params.providers
        ?.filter(Boolean)
        .forEach((p) => query.append("providers", p));
      params.sources
        ?.filter(Boolean)
        .forEach((s) => query.append("sources", s));
      params.types?.filter(Boolean).forEach((t) => query.append("types", t));
      if (params.search) query.set("search", params.search);
      query.set("order_by", params.orderBy ?? "spend");
      query.set("order_dir", params.orderDir ?? "desc");
      query.set("metric_scope", params.metricScope ?? "usage_and_cost");
      query.set("page", String(params.page));
      query.set("page_size", String(params.pageSize));
      const { data } = await statsApi.get<LlmConfigsResponse>(
        "/stats/llm-configs/",
        { params: query },
      );
      return data;
    },
    placeholderData: (prev) => prev,
  });
}

export function useUsersLookup(
  organizationId: string | null | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ["stats", "lookup", "users", organizationId],
    queryFn: async () => {
      const { data } = await statsApi.get<{ data: LookupItem[] }>(
        "/stats/lookup/users",
        {
          headers: {
            "X-Organization-ID": organizationId || undefined,
          },
        },
      );
      return data.data ?? [];
    },
    enabled: enabled && Boolean(organizationId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useTopProjects(
  filters: StatsFilters,
  limit = 5,
  metricScope: MetricScope = "usage_and_cost",
) {
  const query = useQuery({
    queryKey: ["stats", "top-projects", limit, metricScope, filterKey(filters)],
    queryFn: () => {
      const params = buildTopParams({
        ...filters,
        limit,
        sortBy: metricScope === "usage" ? "total_requests" : "total_spend",
      });
      params.set("metric_scope", metricScope);
      return fetchStats<TopProjectsResponse>("/stats/projects/top", params);
    },
  });
  return { ...query, metricScope };
}

export function useProjectsLookup(
  organizationId: string | null | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: ["stats", "lookup", "projects", organizationId],
    queryFn: async () => {
      const { data } = await statsApi.get<{ data: LookupItem[] }>(
        "/stats/lookup/projects",
        {
          headers: {
            "X-Organization-ID": organizationId || undefined,
          },
        },
      );
      return data.data ?? [];
    },
    enabled: enabled && Boolean(organizationId),
    staleTime: 5 * 60 * 1000,
  });
}

export function useOrganizationSubscription(organizationId?: string | null) {
  return useQuery({
    queryKey: [
      "usage",
      "plans",
      "organization",
      "subscription",
      organizationId,
    ],
    queryFn: async () => {
      const { data } = await statsApi.get<PlanInfo>(
        "/usage/plans/organization/subscription",
        {
          headers: {
            "X-Organization-ID": organizationId || undefined,
          },
        },
      );
      return data;
    },
    enabled: Boolean(organizationId),
  });
}

export function useOrganizationQuota(organizationId?: string | null) {
  return useQuery({
    queryKey: ["usage", "organization", organizationId, "quota", "GPU_HOURS"],
    queryFn: async () => {
      const { data } = await usageApi.getOrganizationQuota(
        organizationId,
        "GPU_HOURS",
      );
      return data as OrganizationQuota;
    },
    enabled: Boolean(organizationId),
  });
}

export function useUserPlan(enabled = true) {
  return useQuery({
    queryKey: ["usage", "plans", "user"],
    queryFn: async () => {
      const { data } = await statsApi.get<PlanInfo>("/usage/plans/user");
      return data;
    },
    enabled,
  });
}
